import React, { useState } from "react";
import { motion } from "framer-motion";
import categories from "../data/projects";

function CardFront({ project }) {
  return (
    <div className="flip-face overflow-hidden bg-[#F5EFEB]">
      <img
        src={project.image}
        alt={project.title}
        loading="lazy"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#2A1D15]/75 to-transparent p-6">
        <p className="overline text-[#FDFBF7]/80">{project.occasion}</p>
        <h3 className="mt-2 font-serif-display text-2xl md:text-3xl text-[#FDFBF7] leading-tight">
          {project.title}
        </h3>
      </div>
      <span className="absolute top-4 right-4 overline text-[10px] bg-[#FDFBF7]/85 text-[#8B4513] px-3 py-1.5">
        Flip
      </span>
    </div>
  );
}

function CardBack({ project }) {
  return (
    <div className="flip-face flip-back bg-[#C4B0A2] text-[#2A1D15] p-8 md:p-10 flex flex-col">
      <p className="overline text-[#5C4334]">{project.occasion}</p>
      <h3 className="mt-3 font-serif-display text-3xl md:text-4xl text-[#2A1D15] leading-tight">
        {project.title}
      </h3>
      <p className="mt-5 text-sm md:text-base text-[#2A1D15]/85 font-light leading-relaxed flex-1 overflow-y-auto no-scrollbar">
        {project.description}
      </p>
      <p
        className="mt-5 text-sm md:text-base font-medium text-[#5C4334] tracking-wide"
        data-testid={`gallery-range-${project.slug}`}
      >
        Range: {project.range}
      </p>
      <p className="mt-3 overline text-[#5C4334]">Gifting Concierge</p>
    </div>
  );
}

function FlipCard({ project, index }) {
  const [flipped, setFlipped] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 6) * 0.05 }}
      className="perspective"
      data-testid={`gallery-card-${project.slug}`}
    >
      <button
        type="button"
        onClick={() => setFlipped((v) => !v)}
        onMouseEnter={() => setFlipped(true)}
        onMouseLeave={() => setFlipped(false)}
        aria-label={`Flip card to read about ${project.title}`}
        className="block w-full h-[400px] md:h-[440px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B4513]"
      >
        <div className={`flip-card ${flipped ? "is-flipped" : ""}`}>
          <CardFront project={project} />
          <CardBack project={project} />
        </div>
      </button>
    </motion.div>
  );
}

function CategorySection({ category }) {
  return (
    <div
      className="mt-20 md:mt-24 first:mt-16 md:first:mt-20"
      data-testid={`gallery-category-${category.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <h3 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[#2A1D15] leading-tight">
          {category.label}
        </h3>
        <p className="text-sm md:text-base text-[#5C4334] font-light md:text-right">
          {category.tagline}
        </p>
      </div>
      <div className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {category.products.map((p, i) => (
          <FlipCard key={p.slug} project={p} index={i} />
        ))}
      </div>
    </div>
  );
}

export default function Gallery() {
  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-[#f6ece1]"
    >
      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24">
        <div className="max-w-3xl">
          <p className="overline text-[#8B4513] mb-6" data-testid="gallery-overline">
            Our Work · Some of our gifts
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.98] tracking-tight text-[#2A1D15]">
            A little look at <span className="italic text-[#8B4513]">what we do</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#5C4334] font-light leading-relaxed">
            Hover or tap each card to read the story behind it. Every gift is
            made-to-order, every detail intentional.
          </p>
        </div>

        {categories.map((cat) => (
          <CategorySection key={cat.id} category={cat} />
        ))}

        <p className="mt-16 text-center text-sm font-light text-[#5C4334]">
          And many more — every gift unique, every brief different.
        </p>
      </div>
    </section>
  );
}
